import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { BarChart3, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import AdvancedReportsStatsCards from '../components/reports/AdvancedReportsStatsCards';
import AdvancedReportTypesCard from '../components/reports/AdvancedReportTypesCard';
import AdvancedReportsListCard from '../components/reports/AdvancedReportsListCard';
import AdvancedReportsGenerateDialog from '../components/reports/AdvancedReportsGenerateDialog';

const AdvancedReports = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reports] = useState([]);
  const [reportConfig, setReportConfig] = useState({
    type_rapport: '',
    date_debut: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    date_fin: new Date().toISOString().split('T')[0],
    employees: [],
    sites: [],
    format_export: 'PDF',
    periodicite: 'mensuel',
    inclure_details: true,
    inclure_graphiques: true
  });
  const [generateDialog, setGenerateDialog] = useState(false);

  const now = new Date();
  const analyticsQ = useQuery({
    queryKey: ['reports-analytics', now.getFullYear(), now.getMonth() + 1],
    queryFn: async () => {
      const res = await api.get('v1/reports/monthly-attendance', {
        params: { year: now.getFullYear(), month: now.getMonth() + 1 },
      });
      const summary = res?.data?.summary;
      const byDept = res?.data?.by_department ?? [];
      if (!summary) return { presence_rate: 0, retard_rate: 0, heures_sup_moyennes: 0, anomalies_rate: 0 };
      const total = summary.total_records || 1;
      const avgWorked = byDept.length
        ? byDept.reduce((s, d) => s + (d.avg_worked_hours || 0), 0) / byDept.length
        : 0;
      return {
        presence_rate: summary.attendance_rate ?? 0,
        retard_rate: Math.round((summary.late || 0) / total * 100 * 10) / 10,
        heures_sup_moyennes: Math.round(avgWorked * 10) / 10,
        anomalies_rate: Math.round((summary.absent || 0) / total * 100 * 10) / 10,
      };
    },
  });

  const analytics = analyticsQ.data ?? { presence_rate: 0, retard_rate: 0, heures_sup_moyennes: 0, anomalies_rate: 0 };
  const loading = analyticsQ.isPending;

  const generateReport = async () => {
    try {
      await api.post('v1/reports/generer', null, {
        params: {
          type_rapport: reportConfig.type_rapport,
          date_debut: reportConfig.date_debut,
          date_fin: reportConfig.date_fin,
          format_export: reportConfig.format_export.toLowerCase(),
        },
      });
      toast.success(t('advancedReports.toasts.generateStarted'), {
        description: t('advancedReports.toasts.generateStartedDesc', { type: reportConfig.type_rapport }),
      });
      setGenerateDialog(false);
    } catch (error) {
      toast.error(t('common.error'), { description: t('advancedReports.toasts.generateError') });
    }
  };

  const downloadReport = async (reportId) => {
    try {
      window.open(`v1/reports/${reportId}/download`, '_blank');
      toast.success(t('advancedReports.toasts.downloadStarted'), { description: t('advancedReports.toasts.downloadStartedDesc') });
    } catch (error) {
      toast.error(t('common.error'), { description: t('advancedReports.toasts.downloadError') });
    }
  };

  const shareReport = async (report) => {
    try {
      toast.success(t('advancedReports.toasts.shareSuccess'), { description: t('advancedReports.toasts.shareSuccessDesc', { name: report.nom }) });
    } catch (error) {
      toast.error(t('common.error'), { description: t('advancedReports.toasts.shareError') });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('advancedReports.title')}</h1>
          <p className="text-muted-foreground">{t('advancedReports.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setGenerateDialog(true)}>
            <BarChart3 className="mr-2 h-4 w-4" />
            {t('advancedReports.newReport')}
          </Button>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['reports-analytics'] })} variant="outline" disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('advancedReports.refresh')}
          </Button>
        </div>
      </div>

      <AdvancedReportsStatsCards analytics={analytics} />

      <AdvancedReportTypesCard />

      <AdvancedReportsListCard
        reports={reports}
        downloadReport={downloadReport}
        shareReport={shareReport}
      />

      <AdvancedReportsGenerateDialog
        open={generateDialog}
        onOpenChange={setGenerateDialog}
        reportConfig={reportConfig}
        setReportConfig={setReportConfig}
        generateReport={generateReport}
      />

    </div>
  );
};

export default AdvancedReports;
